import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/Popover';
import { type OrgUnitNode, type OrgUnitType, useOrgUnits } from '../network/org.api';

interface OrgUnitPickerProps {
  nodes?: OrgUnitNode[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  unitType?: OrgUnitType | OrgUnitType[];
  excludeSubtreeOfId?: string;
  disabled?: boolean;
}

interface FlatOrgUnit {
  id: string;
  name: string;
  code: string;
  fullPath: string;
  unitType: OrgUnitType;
}

export function OrgUnitPicker({
  nodes: passedNodes,
  value,
  onChange,
  placeholder = 'Select branch...',
  unitType,
  excludeSubtreeOfId,
  disabled,
}: OrgUnitPickerProps) {
  const { data: fetchedNodes } = useOrgUnits();
  const nodes = passedNodes || fetchedNodes || [];
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const isTypeAllowed = React.useCallback(
    (itemType: OrgUnitType) => {
      if (!unitType) return true;
      if (Array.isArray(unitType)) return unitType.includes(itemType);
      if (unitType === 'BRANCH') {
        return (
          itemType === 'BRANCH' ||
          itemType === 'HEAD_OFFICE' ||
          itemType === 'SUB_BRANCH' ||
          itemType === 'SERVICE_POINT'
        );
      }
      return itemType === unitType;
    },
    [unitType],
  );

  const flatten = React.useCallback(
    (items: OrgUnitNode[], path: string = '', isExcluded: boolean = false): FlatOrgUnit[] => {
      let flat: FlatOrgUnit[] = [];
      for (const item of items) {
        const currentlyExcluded = isExcluded || item.id === excludeSubtreeOfId;
        const currentPath = path ? `${path} > ${item.name}` : item.name;

        if (!currentlyExcluded && isTypeAllowed(item.unitType)) {
          flat.push({
            id: item.id,
            name: item.name,
            code: item.code,
            fullPath: currentPath,
            unitType: item.unitType,
          });
        }

        if (item.children?.length) {
          flat = [...flat, ...flatten(item.children, currentPath, currentlyExcluded)];
        }
      }
      return flat;
    },
    [excludeSubtreeOfId, isTypeAllowed],
  );

  const flatNodes = React.useMemo(() => flatten(nodes), [nodes, flatten]);

  const filteredNodes = React.useMemo(() => {
    if (!search.trim()) return flatNodes;
    const q = search.toLowerCase();
    return flatNodes.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.code.toLowerCase().includes(q) ||
        n.fullPath.toLowerCase().includes(q),
    );
  }, [flatNodes, search]);

  const selectedNode = React.useMemo(() => flatNodes.find((n) => n.id === value), [flatNodes, value]);
  const listboxId = React.useId();
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [search, open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (filteredNodes.length > 0 ? Math.min(prev + 1, filteredNodes.length - 1) : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredNodes[activeIndex]) {
        onChange(filteredNodes[activeIndex].id);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full justify-between bg-surface-3 font-normal"
          disabled={disabled}
        >
          {selectedNode ? (
            <span className="truncate">{selectedNode.fullPath}</span>
          ) : (
            <span className="text-text-muted">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 min-w-[240px]" align="start">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="h-4 w-4 text-text-muted shrink-0" />
            <Input
              placeholder="Search units..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-activedescendant={
                filteredNodes[activeIndex] ? `${listboxId}-option-${filteredNodes[activeIndex].id}` : undefined
              }
              className="flex-1 h-8 border-none bg-transparent shadow-none focus:ring-0 focus:border-transparent px-0 placeholder:text-text-muted"
            />
          </div>
          <div id={listboxId} role="listbox" className="max-h-[300px] overflow-y-auto p-1">
            {filteredNodes.length === 0 ? (
              <div className="py-6 text-center text-sm text-text-muted">No unit found.</div>
            ) : (
              filteredNodes.map((node, index) => (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  key={node.id}
                  id={`${listboxId}-option-${node.id}`}
                  role="option"
                  aria-selected={value === node.id || activeIndex === index}
                  onClick={() => {
                    onChange(node.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full justify-between text-left rounded-sm px-2',
                    'hover:bg-accent-500/10 hover:text-accent-500',
                    (value === node.id || activeIndex === index) && 'bg-accent-500/10 text-accent-500 font-medium',
                  )}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-text-primary">{node.name}</span>
                      <span className="text-caption uppercase bg-surface-4 px-1 rounded text-text-muted border border-border">
                        {node.unitType}
                      </span>
                    </div>
                    <span className="text-xs text-text-muted">{node.fullPath}</span>
                  </div>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4',
                      value === node.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </Button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
